import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { controlCenterApi } from '@/lib/api'
import StatusBadge from '@/components/StatusBadge'
import PageHeader from '@/components/PageHeader'

// Shape of an `agent_registry` row as returned by GET /api/platform/agent-registry.
// Deployed Maestro instances (cloud authority + local subordinates) self-register
// + heartbeat into this table via the Phase B0 reporter.
type AgentEntry = {
  id?: string
  name?: string
  agent_type?: string
  version?: string
  status?: string
  health_status?: string | null
  last_health_check?: string | null
  capabilities?: string[]
  config?: {
    federation_role?: string
    active_platforms?: string[]
    health_url?: string | null
  } | null
}

// The list endpoint wrapper has varied across MOS revisions — accept the common shapes.
type AgentRegistryResponse =
  | { success?: boolean; entries?: AgentEntry[]; agents?: AgentEntry[]; data?: AgentEntry[] }
  | AgentEntry[]

function extractEntries(data: AgentRegistryResponse | undefined): AgentEntry[] {
  if (!data) return []
  if (Array.isArray(data)) return data
  return data.entries ?? data.agents ?? data.data ?? []
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return String(iso)
  const secs = Math.round((Date.now() - t) / 1000)
  if (secs < 60) return `${secs}s ago`
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`
  return `${Math.round(secs / 86400)}d ago`
}

export default function Instances() {
  const { data, isLoading, isError } = useQuery<AgentRegistryResponse>({
    queryKey: ['platform', 'agent-registry'],
    queryFn: controlCenterApi.listAgentRegistry,
    // Deployed instances heartbeat ~every 60s; refetch so "last seen" stays live.
    refetchInterval: 30_000,
  })

  const [typeFilter, setTypeFilter] = useState<string>('maestro')

  const entries = extractEntries(data)
  const types = useMemo(() => {
    const set = new Set<string>()
    for (const e of entries) set.add(String(e.agent_type || 'unknown'))
    return Array.from(set).sort()
  }, [entries])

  const filtered = entries.filter((e) => {
    if (typeFilter === 'all') return true
    return String(e.agent_type) === typeFilter
  })

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Instances"
        description={`${entries.length} registered agents/instances. Source: agent_registry. Maestro instances self-report health via the Control Center reporter.`}
        actions={
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded-md border border-border bg-card px-3 py-1.5 text-sm"
          >
            <option value="all">All types</option>
            {types.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        }
      />

      {isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Failed to load the agent registry. Confirm aios-backend is running and you&apos;re authenticated.
        </div>
      ) : null}

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="text-left">
              <th className="px-4 py-2.5 font-medium">Name</th>
              <th className="px-4 py-2.5 font-medium">Type</th>
              <th className="px-4 py-2.5 font-medium">Version</th>
              <th className="px-4 py-2.5 font-medium">Health</th>
              <th className="px-4 py-2.5 font-medium">Role</th>
              <th className="px-4 py-2.5 font-medium">Platforms</th>
              <th className="px-4 py-2.5 font-medium">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                  No instances registered yet.
                </td>
              </tr>
            ) : (
              filtered.map((e, i) => (
                <tr key={e.id ?? e.name ?? i} className="border-t border-border">
                  <td className="px-4 py-2.5 font-medium">{e.name ?? '—'}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{e.agent_type ?? '—'}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{e.version ?? '—'}</td>
                  <td className="px-4 py-2.5">
                    <StatusBadge status={e.health_status ?? e.status ?? 'unknown'} />
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">
                    {e.config?.federation_role ?? '—'}
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">
                    {(e.config?.active_platforms ?? []).join(', ') || '—'}
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">
                    {relativeTime(e.last_health_check)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
